/**
 * Content API client
 * Fetches page content from Umbraco CMS v2 ContentController.
 * The v2 API returns { id, type, blockGridLayout } directly (no compositions wrapper).
 */

import { getEnv } from "@/config/env";
import { defaultHeaders, getJson, withCacheBust } from "@/api/http";
import type { BlockGridLayout } from "@/types/content";
import type { Result } from "@/types/layout";
import { isProductPage } from "@/types/product";
import type {
  MetaDataComposition,
  OpenGraphComposition,
  TwitterCardComposition,
  JsonLdComposition,
  CustomPublishDateComposition,
} from "@/types/seo";

const CONTENT_ENDPOINT = "/content";

/** Response shape from the v2 content endpoint */
export interface BlockGridContentResponse {
  id: string;
  type: string;
  blockGridLayout: BlockGridLayout | null;
  /** Maps culture code → localized URL for this page in each published culture */
  alternateUrls?: Record<string, string>;
  /** SEO compositions (populated when content type implements them) */
  metaData?: MetaDataComposition;
  openGraph?: OpenGraphComposition;
  twitterCard?: TwitterCardComposition;
  jsonLd?: JsonLdComposition;
  customPublishDate?: CustomPublishDateComposition;
}

/**
 * Requests started ahead of the component that needs them (see
 * prefetchContent), keyed by API URL plus the store-context headers, so an
 * answer is only ever handed to an identical request. One-shot: the first
 * fetch of the same request takes it. An entry nobody takes in time (the page
 * turned out not to need content, e.g. checkout) expires rather than serve a
 * later visit an old answer.
 */
const PREFETCH_TTL_MS = 30_000;
const prefetched = new Map<
  string,
  { promise: Promise<Result<unknown>>; startedAt: number }
>();

function prefetchKey(apiUrl: string): string {
  return `${apiUrl}\n${JSON.stringify(defaultHeaders())}`;
}

function liveEntry(key: string) {
  const entry = prefetched.get(key);
  if (entry && Date.now() - entry.startedAt > PREFETCH_TTL_MS) {
    prefetched.delete(key);
    return undefined;
  }
  return entry;
}

function prefetch<T>(apiUrl: string): Promise<Result<T>> {
  const key = prefetchKey(apiUrl);
  const existing = liveEntry(key);
  if (existing) return existing.promise as Promise<Result<T>>;
  const promise = getJson<T>(apiUrl);
  prefetched.set(key, { promise, startedAt: Date.now() });
  return promise;
}

/**
 * GETs through the prefetch: an in-flight or settled prefetch of the same
 * request is taken instead of a new request. The caller's signal cannot
 * cancel a prefetch it did not start; callers already ignore a result that
 * arrives after they aborted.
 */
function getJsonOrPrefetched<T>(
  apiUrl: string,
  signal?: AbortSignal
): Promise<Result<T>> {
  const key = prefetchKey(apiUrl);
  const entry = liveEntry(key);
  if (entry) {
    prefetched.delete(key);
    return entry.promise as Promise<Result<T>>;
  }
  return getJson<T>(apiUrl, signal ? { signal } : undefined);
}

function contentApiUrl(url: string, previewId?: string): string {
  const env = getEnv();
  if (previewId) {
    return withCacheBust(
      `${env.apiBaseUrl}${CONTENT_ENDPOINT}?previewId=${encodeURIComponent(previewId)}`
    );
  }
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  return withCacheBust(
    `${env.apiBaseUrl}${CONTENT_ENDPOINT}?url=${encodeURIComponent(normalizedUrl)}`
  );
}

function contentByKeyApiUrl(key: string): string {
  return withCacheBust(
    `${getEnv().apiBaseUrl}${CONTENT_ENDPOINT}/${encodeURIComponent(key)}`
  );
}

/**
 * Starts the content request for a page before the route that renders it
 * exists. The routes are only known once the layout has arrived (their slugs
 * are localized), so without this the content request queued a whole API
 * round trip behind the layout, delaying every page's first content (LCP).
 * A product page's full data, which needs the content's key, follows as soon
 * as the content arrives.
 *
 * Takes the same arguments as fetchContent, which then picks the answer up.
 */
export function prefetchContent(
  url: string,
  options?: { previewId?: string }
): void {
  void prefetch<BlockGridContentResponse>(
    contentApiUrl(url, options?.previewId)
  ).then((result) => {
    if (result.ok && isProductPage(result.data)) {
      void prefetch(contentByKeyApiUrl(result.data.id));
    }
  });
}

/**
 * Fetches page content from the v2 API.
 *
 * @param url - The page URL path (e.g., "/", "/about", "/products/device-x")
 * @param signal - Optional AbortSignal
 * @param options.previewId - Optional Umbraco content node ID for preview (fetches drafts)
 */
export async function fetchContent(
  url: string,
  signal?: AbortSignal,
  options?: { previewId?: string }
): Promise<Result<BlockGridContentResponse>> {
  return getJsonOrPrefetched<BlockGridContentResponse>(
    contentApiUrl(url, options?.previewId),
    signal
  );
}

/**
 * Fetches full content data by content key (ShortGuid).
 * Returns the complete view model including compositions (product data, navigation, etc.).
 *
 * @param key - The content key (ShortGuid format, e.g., from BlockGridContentResponse.id)
 */
export async function fetchContentByKey<T = unknown>(
  key: string,
  signal?: AbortSignal
): Promise<Result<T>> {
  return getJsonOrPrefetched<T>(contentByKeyApiUrl(key), signal);
}

/**
 * Fetches page content and extracts only the blockGridLayout.
 *
 * @param url - The page URL path
 * @returns The block grid layout or an error if not present
 */
export async function fetchBlockGridContent(
  url: string
): Promise<Result<BlockGridLayout>> {
  const result = await fetchContent(url);

  if (!result.ok) {
    return result;
  }

  const blockGridLayout = result.data.blockGridLayout;

  if (!blockGridLayout) {
    return {
      ok: false,
      error: "Page does not have a block grid layout",
    };
  }

  return {
    ok: true,
    data: blockGridLayout,
  };
}
